import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DotsVertical,
  EmptyState,
  FileUpload,
  Menu,
  Modal,
  Table,
  Tag,
  Trash01,
} from '@opendate-app/setlist';

import { DOCUMENT_CATEGORY_LABELS } from '../../Grants/grantStages';
import { REFRESH_ERROR } from '../refreshError';

const MAX_SIZE = 25 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
const HINT = 'PDF, DOCX, XLSX or images (max. 25MB)';
const SAVE_FAILED = "Couldn't save the change. Try again.";
const LOAD_FAILED = { displayMessage: "Couldn't load documents." };
const CATEGORY_COLORS = {
  application: 'blue',
  award_letter: 'success',
  report: 'purple',
  supporting: 'gray',
};

const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-xl)' };
const pickerRow = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--setlist-spacing-md)' };
const pickerLabel = { margin: 0, color: 'var(--sl-text-secondary)', fontSize: 'var(--setlist-text-sm-size)' };

const extensionOf = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

const validationError = (file) => {
  if (file.size >= MAX_SIZE) return 'File is too large (max. 25MB)';
  const allowed = ALLOWED_EXTENSIONS.includes(extensionOf(file.name)) || (file.type || '').startsWith('image/');
  return allowed ? null : 'File type not allowed';
};

const serverError = (err) => {
  const data = err && err.response && err.response.data;
  if (err && err.response && err.response.status === 422 && Array.isArray(data) && data.length > 0) {
    return data.join(', ');
  }
  return SAVE_FAILED;
};

function CategoryBadge({ category }) {
  const label = DOCUMENT_CATEGORY_LABELS[category];
  if (!label) return <Badge color="gray">—</Badge>;
  return <Badge color={CATEGORY_COLORS[category]}>{label}</Badge>;
}

CategoryBadge.propTypes = { category: PropTypes.string };

function GrantDocumentsTab({
  team,
  csrfToken,
  grant,
  canManage = false,
  refreshGrant,
  onDocumentsChange,
  onError,
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [category, setCategory] = useState('application');
  const [queue, setQueue] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  const nextUploadId = useRef(0);

  useEffect(() => () => { mounted.current = false; }, []);

  const attachmentsUrl = `/teams/${team.id}/crm/grants/${grant.id}/attachments.json`;
  const attachmentUrl = (doc) => `/teams/${team.id}/crm/grants/${grant.id}/attachments/${doc.id}.json`;
  const requestConfig = { headers: { 'X-CSRF-TOKEN': csrfToken } };

  const loadDocuments = useCallback(async () => {
    const response = await axios.get(attachmentsUrl, { headers: { 'X-CSRF-TOKEN': csrfToken } });
    if (!mounted.current) return;
    const list = response.data || [];
    setLoadFailed(false);
    setDocuments(list);
    if (onDocumentsChange) onDocumentsChange(list.length);
  }, [attachmentsUrl, csrfToken, onDocumentsChange]);

  const reportLoadFailure = useCallback(() => {
    if (mounted.current) setLoadFailed(true);
    onError(LOAD_FAILED);
  }, [onError]);

  useEffect(() => {
    setLoading(true);
    loadDocuments()
      .catch(reportLoadFailure)
      .finally(() => mounted.current && setLoading(false));
  }, [loadDocuments, reportLoadFailure]);

  const refreshAfterChange = () => Promise.all([
    loadDocuments().catch(reportLoadFailure),
    refreshGrant ? refreshGrant().catch(() => onError(REFRESH_ERROR)) : null,
  ]);

  const updateQueued = (id, changes) => {
    if (!mounted.current) return;
    setQueue((current) => current.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)));
  };

  const removeQueued = (id) => {
    if (!mounted.current) return;
    setQueue((current) => current.filter((entry) => entry.id !== id));
  };

  const upload = async (file, id) => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    try {
      await axios.post(attachmentsUrl, form, {
        ...requestConfig,
        onUploadProgress: ({ loaded, total }) => {
          if (total) updateQueued(id, { progress: (loaded / total) * 100 });
        },
      });
      removeQueued(id);
      await refreshAfterChange();
    } catch (err) {
      updateQueued(id, { error: serverError(err) });
    }
  };

  const handleFilesSelected = (files) => {
    files.forEach((file) => {
      nextUploadId.current += 1;
      const id = `upload-${nextUploadId.current}`;
      const error = validationError(file);
      const entry = { id, name: file.name, size: file.size, progress: error ? undefined : 0, error };
      setQueue((current) => [...current, entry]);
      if (!error) upload(file, id);
    });
  };

  const handleCategoryChange = (value) => (selected) => {
    if (selected) setCategory(value);
  };

  const closeDelete = () => {
    if (saving) return;
    setDeleting(null);
  };

  const confirmDelete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.delete(attachmentUrl(deleting), requestConfig);
      setDeleting(null);
      await refreshAfterChange();
    } catch (err) {
      setDeleting(null);
      onError(err);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const columns = [
    {
      key: 'file_name',
      header: 'File name',
      render: (doc) => (
        <Button as="a" variant="link-color" href={doc.download_url}>{doc.file_name}</Button>
      ),
    },
    { key: 'category', header: 'Type', render: (doc) => <CategoryBadge category={doc.category} /> },
    { key: 'uploaded_by', header: 'Uploaded by', render: (doc) => doc.uploaded_by || '—' },
    { key: 'human_created_at', header: 'Date', render: (doc) => doc.human_created_at },
    { key: 'human_size', header: 'Size', render: (doc) => doc.human_size },
  ];

  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      width: '48px',
      render: (doc) => (
        <Menu
          placement="bottom-end"
          trigger={(
            <Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${doc.file_name}`} />
          )}
          items={[
            { label: 'Delete', icon: Trash01, destructive: true, onClick: () => setDeleting(doc) },
          ]}
        />
      ),
    });
  }

  const showEmpty = !loading && !loadFailed && documents.length === 0;

  return (
    <div style={stack}>
      {canManage && (
        <Card header={<CardHeader title="Upload document" />}>
          <div style={stack}>
            <div style={pickerRow} role="group" aria-labelledby="grant-document-category">
              <p id="grant-document-category" style={pickerLabel}>Category</p>
              {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
                <Tag
                  key={value}
                  selectable
                  selected={category === value}
                  onSelectedChange={handleCategoryChange(value)}
                >
                  {label}
                </Tag>
              ))}
            </div>
            <FileUpload
              hint={HINT}
              multiple={false}
              files={queue}
              onFilesSelected={handleFilesSelected}
              onRemove={(entry) => removeQueued(entry.id)}
            />
          </div>
        </Card>
      )}

      <Card padding="none" header={<CardHeader title="Documents" />}>
        {showEmpty ? (
          <EmptyState size="sm" title="No documents yet. Upload the application, award letter or reports." />
        ) : (
          <Table columns={columns} data={documents} loading={loading} getRowKey={(doc) => doc.id} />
        )}
      </Card>

      <Modal
        open={Boolean(deleting)}
        onClose={closeDelete}
        type="error"
        title="Delete document?"
        description={deleting ? `${deleting.file_name} will be permanently removed.` : undefined}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />
    </div>
  );
}

GrantDocumentsTab.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number }).isRequired,
  csrfToken: PropTypes.string,
  grant: PropTypes.shape({ id: PropTypes.number }).isRequired,
  canManage: PropTypes.bool,
  refreshGrant: PropTypes.func,
  onDocumentsChange: PropTypes.func,
  onError: PropTypes.func.isRequired,
};

export default GrantDocumentsTab;
